import {
  Component,
  computed,
  effect,
  inject,
  input,
  numberAttribute,
  signal,
  untracked,
} from '@angular/core';
import { Router } from '@angular/router';
import { agentCounts } from '../../domain/agent-counts';
import { apiErrorMessage } from '../../services/api-error';
import { MissionService } from '../../services/mission.service';
import { PlayerSessionService } from '../../services/player-session.service';
import { reloadEvery } from '../../services/polling';
import { PublicOperationService } from '../../services/public-operation.service';
import { ConfirmDialog } from '../../shared/confirm-dialog/confirm-dialog';
import { Countdown } from '../../shared/countdown/countdown';
import { Dossier } from '../../shared/dossier/dossier';
import { GameStatus } from '../../shared/game-status/game-status';
import { PageTitle } from '../../shared/page-title/page-title';
import { Roster } from '../../shared/roster/roster';
import { StatePanel } from '../../shared/state-panel/state-panel';

// Salle de briefing : lobby d'un agent inscrit, tant que sa mission est READY (TD-011).
@Component({
  selector: 'app-briefing-page',
  imports: [ConfirmDialog, Countdown, Dossier, GameStatus, PageTitle, Roster, StatePanel],
  templateUrl: './briefing-page.html',
})
export class BriefingPage {
  readonly missionId = input.required({ transform: numberAttribute });

  private readonly router = inject(Router);
  private readonly missions = inject(MissionService);
  private readonly session = inject(PlayerSessionService);

  protected readonly mission = this.missions.mission(() => this.missionId());
  protected readonly operation = inject(PublicOperationService).operation(
    () => this.mission.value()?.operationPublicId,
  );
  protected readonly counts = computed(() => agentCounts(this.operation.value()?.ranking ?? []));
  protected readonly selfCodeName = computed(() => this.session.codeNameFor(this.missionId()));
  protected readonly loadError = computed(() => {
    const error = this.mission.error() ?? this.operation.error();
    return error ? apiErrorMessage(error) : null;
  });

  protected readonly confirmingLeave = signal(false);
  protected readonly leaving = signal(false);
  protected readonly leaveError = signal<string | null>(null);

  constructor() {
    // Pas encore de temps reel (lot 10) : le lobby interroge l'API.
    reloadEvery(this.mission, 5000);
    reloadEvery(this.operation, 10_000);

    // Mission demarree : la route de la mission mene a l'ecran de son statut.
    effect(() => {
      const mission = this.mission.value();
      if (mission && mission.statusCode !== 'READY') {
        untracked(() => this.router.navigate(['/missions', this.missionId()]));
      }
    });
  }

  protected async leave(): Promise<void> {
    this.leaving.set(true);
    this.leaveError.set(null);
    try {
      await this.missions.leave(this.missionId());
      this.router.navigate(['/']);
    } catch (error) {
      this.leaveError.set(apiErrorMessage(error));
    } finally {
      this.leaving.set(false);
    }
  }
}
